// frontend/src/context/CronometroContext.jsx

import React, { createContext, useContext, useState, useEffect } from 'react';
import kanbanService from '../services/kanbanService';
import { toast } from 'react-toastify';

const CronometroContext = createContext();

export const useCronometro = () => {
  const context = useContext(CronometroContext);
  if (!context) {
    throw new Error('useCronometro debe ser usado dentro de CronometroProvider');
  }
  return context;
};

export const CronometroProvider = ({ children }) => {
  const [tareasActivas, setTareasActivas] = useState([]);
  const [timers, setTimers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  // ⭐ Nuevo: callbacks para notificar cambios
  const [updateCallbacks, setUpdateCallbacks] = useState([]);

  // Cargar tareas de Kanban con cronómetros activos
  const cargarTareasActivas = async () => {
    try {
      const boards = await kanbanService.getAllBoards().catch(() => ({ boards: [] }));

      // Procesar solo tareas de Kanban
      let tareasKanbanActivas = [];
      const boardsList = Array.isArray(boards) ? boards : (boards?.boards || []);

      // Obtener tareas de todos los tableros
      for (const board of boardsList) {
        try {
          const tasksData = await kanbanService.getTasks(board.id);
          const boardTasks = Array.isArray(tasksData)
            ? tasksData
            : (tasksData?.tasks || []);
          const activeBoardTasks = boardTasks.filter(task => task.is_timer_active);
          tareasKanbanActivas = [...tareasKanbanActivas, ...activeBoardTasks];
        } catch (error) {
          console.error(`Error cargando tareas del tablero ${board.id}:`, error);
        }
      }

      setTareasActivas(tareasKanbanActivas);
    } catch (error) {
      console.error('Error cargando tareas activas:', error);
    }
  };

  // Actualizar timers cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setTimers(prev => {
        const newTimers = {};
        tareasActivas.forEach(tarea => {
          if (tarea.is_timer_active && tarea.timer_started_at) {
            const startTime = new Date(tarea.timer_started_at);
            const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
            newTimers[tarea.id] = tarea.total_time_seconds + elapsed;
          }
        });
        return newTimers;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [tareasActivas]);

  // Cargar tareas activas al montar
  useEffect(() => {
    cargarTareasActivas();
    // Actualizar cada 30 segundos para sincronizar con el servidor
    const syncInterval = setInterval(cargarTareasActivas, 30000);
    return () => clearInterval(syncInterval);
  }, []);

  // Formatear tiempo en HH:MM:SS
  const formatTime = (seconds) => {
    const totalSeconds = seconds || 0;
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // ⭐ Registrar callback para recibir actualizaciones
  const registrarUpdateCallback = (callback) => {
    setUpdateCallbacks(prev => [...prev, callback]);
    // Retornar función para desregistrar
    return () => {
      setUpdateCallbacks(prev => prev.filter(cb => cb !== callback));
    };
  };

  // ⭐ Notificar a todos los callbacks registrados
  const notificarCambio = (tipo, tareaId, data) => {
    updateCallbacks.forEach(callback => {
      try {
        callback(tipo, tareaId, data);
      } catch (error) {
        console.error('Error en callback de actualización:', error);
      }
    });
  };

  // Detener cronómetro de Kanban
  const detenerCronometro = async (tareaId) => {
    setIsLoading(true);
    try {
      const response = await kanbanService.stopTimer(tareaId);

      // Actualizar el tiempo total y remover de activas
      setTareasActivas(prev => prev.filter(t => t.id !== tareaId));

      // Actualizar el timer con el tiempo final
      if (response.total_time_seconds) {
        setTimers(prev => ({
          ...prev,
          [tareaId]: response.total_time_seconds
        }));
      }

      // ⭐ Notificar que se detuvo con el tiempo total
      notificarCambio('detener', tareaId, {
        total_time_seconds: response.total_time_seconds,
        is_timer_active: false,
        timer_started_at: null
      });

      return response;
    } catch (error) {
      console.error('Error deteniendo cronómetro:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Refrescar tareas activas manualmente
  const refrescarTareasActivas = () => {
    cargarTareasActivas();
  };

  // ⭐ Iniciar cronómetro (agregar a tareasActivas y notificar)
  const iniciarCronometro = async (tareaId, tareaData) => {
    try {
      // Agregar a tareasActivas si no está
      setTareasActivas(prev => {
        const exists = prev.find(t => t.id === tareaId);
        if (exists) return prev;
        return [...prev, tareaData];
      });

      // Inicializar el timer
      setTimers(prev => ({
        ...prev,
        [tareaId]: tareaData.total_time_seconds || 0
      }));

      // Notificar a los componentes suscritos
      notificarCambio('iniciar', tareaId, {
        is_timer_active: true,
        timer_started_at: tareaData.timer_started_at,
        timer_active_user_name: tareaData.timer_active_user_name
      });
    } catch (error) {
      console.error('Error inicializando cronómetro en contexto:', error);
    }
  };

  const value = {
    tareasActivas,
    timers,
    formatTime,
    detenerCronometro,
    refrescarTareasActivas,
    iniciarCronometro, // ⭐ Exponer método para iniciar
    registrarUpdateCallback, // ⭐ Exponer para que componentes se suscriban
    isLoading
  };

  return (
    <CronometroContext.Provider value={value}>
      {children}
    </CronometroContext.Provider>
  );
};